import { useState } from 'react';
import { SalesType } from '@client/new/common/types';
import { OTP } from '../../collections/collections.domain';

import * as z from 'zod';
import { ZodObject, ZodTypeAny } from 'zod';
import { Casted } from '../../../helpers/types';
import { keys } from '../../../helpers/general';
import { ProductOption } from './useOtpProducts';
export const urlSourceOptions = [
  'facebook',
  'instagram',
  'google',
  'tiktok',
  'snapchat',
  'pinterest',
  'email',
  'website',
  'other',
] as const;

export type SourceType = (typeof urlSourceOptions)[number];

type EmptyObject = Record<PropertyKey, unknown>;

type ZodSchemaMapper<X extends EmptyObject> = ZodObject<{
  [K in keyof X]: ZodTypeAny;
}>;

export const ensureSchemaType =
  <T extends Record<any, any>>() =>
  <T2 extends ZodSchemaMapper<T> = ZodSchemaMapper<T>>(schema: T2) =>
    schema;

export type URLForm = {
  name: string;
  source: SourceType;
  productType: SalesType;
};

const toStringSchema = (message: string) =>
  z
    .string({
      required_error: message,
    })
    .nonempty({ message });

const schema = ensureSchemaType<URLForm>()(
  z.object({
    name: toStringSchema('Please provide campaign name'),
    source: z.enum(urlSourceOptions, {
      errorMap: () => ({ message: 'Please select campaign source' }),
    }),
    productType: toStringSchema('Please select a product to advertise'),
  }),
);
type FormErrors = Partial<Casted<URLForm & { otp: OTP }, string>>;

let _data: Partial<URLForm> = {
  name: '',
  productType: '' as any,
  source: '' as any,
};

let _otp: ProductOption | undefined = undefined;
let _started = false;
export const useURLBuilderState = () => {
  const [otp, _setOtp] = useState<ProductOption | undefined>(_otp);
  const [started, setStarted] = useState(_started);

  const [isFormDirty, setIsFormDirty] = useState(false);
  const [data, _setData] = useState<Partial<URLForm>>(_data);

  const [errors, setErrors] = useState<FormErrors>({});
  const set =
    <T extends keyof URLForm>(name: T) =>
    (e: { target: { value: URLForm[T] } }) => {
      _setData((p) => {
        const next = { ...p, [name]: e.target.value };
        if (isFormDirty) parse(next); // dirty form
        _data = next;
        return next;
      });
    };

  const parse = (d: Partial<URLForm>) => {
    const res = schema.safeParse(d);
    setIsFormDirty(true);
    const otpError: null | string =
      data.productType === 'otp' && !otp
        ? 'Please select one time purchase product'
        : null;

    let nextErrors: FormErrors = {};
    if (!res.success) {
      const { errors } = (res as any).error;
      nextErrors = {
        ...Object.fromEntries(errors.map((v: any) => [v.path[0], v.message])),
      };
    }
    if (otpError) nextErrors.otp = otpError;
    setErrors(nextErrors);
    return keys(nextErrors).length === 0;
  };

  return {
    otp,
    setOtp: (v: ProductOption | undefined) => {
      _otp = v;
      _setOtp(v);
    },
    isFormDirty,
    setIsFormDirty,
    data,
    set,
    parse,
    errors,
    started,
    setStarted: (v: boolean) => {
      _started = v;
      setStarted(v);
    },
  };
};

export type UseURLBuilderState = ReturnType<typeof useURLBuilderState>;
